import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface ProductInteraction {
  productId: number;
  price: number;
  unitsAddedToCart: number;
}

interface LastWhimInteractionsState {
  productsAddedToCart: ProductInteraction[];
  addProductAddedToCart: (
    productId: number,
    quantity: number,
    price: number,
  ) => void;
  productIdsOfPagesChecked: number[];
  addProductCheckedOut: (productId: number) => void;
  getLastWhimInteractionSummary: () => {
    totalMoney: number;
    totalUnits: number;
    totalProducts: number;
  };
  cleanValues: () => void;
}

export const useLastWhimInteractionStore = create<LastWhimInteractionsState>()(
  persist(
    (set, get) => ({
      productsAddedToCart: [],
      productIdsOfPagesChecked: [],
      totalMoneyAddedToCart: 0,
      addProductAddedToCart: (
        productId: number,
        quantity: number,
        price: number,
      ) => {
        set((state) => {
          const existingProduct = state.productsAddedToCart.find(
            (product) => product.productId === productId,
          );
          if (existingProduct) {
            return {
              productsAddedToCart: state.productsAddedToCart.map((product) =>
                product.productId === productId
                  ? {
                      ...product,
                      unitsAddedToCart: Math.max(
                        product.unitsAddedToCart + quantity,
                        0,
                      ),
                      price,
                    }
                  : product,
              ),
            };
          } else {
            return {
              productsAddedToCart: [
                ...state.productsAddedToCart,
                { productId, unitsAddedToCart: quantity, price },
              ],
            };
          }
        });
      },
      addProductCheckedOut: (productId: number) => {
        set((state) => ({
          productIdsOfPagesChecked: Array.from(
            new Set([...state.productIdsOfPagesChecked, productId]),
          ),
        }));
      },
      getLastWhimInteractionSummary: () => {
        const state = get();
        let totalMoney = 0;
        let totalUnits = 0;
        let totalProducts = 0;
        state.productsAddedToCart.forEach((product) => {
          totalMoney += product.unitsAddedToCart * product.price;
          totalUnits += product.unitsAddedToCart;
          if (product.unitsAddedToCart > 0) {
            totalProducts += 1;
          }
        });
        return {
          totalMoney,
          totalUnits,
          totalProducts,
        };
      },
      cleanValues: () => {
        set(() => ({
          productsAddedToCart: [],
          unitsAddedToCart: 0,
          productIdsOfPagesChecked: [],
        }));
      },
    }),
    {
      name: 'products-interaction-storage', // unique name
      partialize: (state) => ({
        productsAddedToCart: state.productsAddedToCart,
        productIdsOfPagesChecked: state.productIdsOfPagesChecked,
      }),
    },
  ),
);
